/*
 * Geocoding shared by the property, agent and office searches.
 * Free text loses the characters that could change the API route or pose as the internal coordinate
 * form, and is URI-encoded by the API accessor. A search with no usable text or no match falls back
 * to the visitor's default location, so a result always has a place with a viewport; transient
 * service failures still reject so callers can retry.
 * Geocode requests share one abort controller, so a search superseded while it waits for the
 * visitor's location rejects as aborted instead of starting a fallback that would cancel the newer search.
 */
import { geoCode } from '../api';
import { getGeoLocation } from '../utility';

export const geocodeQuery = (keyword: unknown): string =>
  String(keyword ?? '').replace(/[\\/?#%{}]/g, ' ').replace(/\s+/g, ' ').trim();

let generation = 0;
const superseded = () => Object.assign(new Error('Geocoding was superseded by a newer search.'), { name: 'AbortError' });

async function geocodeDefaultLocation(search: number) {
  const { GeoLocation_Longitude, GeoLocation_Latitude } = await getGeoLocation();
  if (search !== generation) { throw superseded(); }
  return geoCode.getGeoCode(`{"lat":${GeoLocation_Latitude}, "lng":${GeoLocation_Longitude}}`);
}

const hasPlace = results => Array.isArray(results) && results.some(result => result?.geometry?.viewport);

export async function getGeoCode(keyword) {
  const search = ++generation;
  const query = geocodeQuery(keyword);
  const results = query ? await geoCode.getGeoCode(query) : null;
  if (hasPlace(results)) { return results; }
  const fallback = await geocodeDefaultLocation(search);
  if (!hasPlace(fallback)) { throw new Error('Geocoding returned no location.'); }
  return fallback;
}

// The city result when present, otherwise the first result that has a map viewport.
export const geocodePlace = results =>
  results.find(result => result?.types?.includes('locality') && result.geometry?.viewport)
  || results.find(result => result?.geometry?.viewport);
